import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { useLocation, useNavigationType } from "react-router-dom";
import Lenis from "lenis";
import { useHomeSplashScroll } from "../context/HomeSplashScrollContext";

const LenisContext = createContext<Lenis | null>(null);

export function useLenis() {
  return useContext(LenisContext);
}

const NAVBAR_OFFSET = 100;
const scrollPositions = new Map<string, number>();

/**
 * Smooth scroll có quán tính (Lenis): sau khi thả bánh xe, vị trí còn “trôi” nhẹ rồi mới dừng hẳn —
 * trong thiết kế web thường gọi là smooth scroll / inertial scrolling / “scroll mượt có quán tính”.
 */
export function SmoothScroll({ children }: { children: ReactNode }) {
  const [lenis, setLenis] = useState<Lenis | null>(null);
  const location = useLocation();
  const navigationType = useNavigationType();
  const { homeHashScrollBlocked } = useHomeSplashScroll();

  useEffect(() => {
    const instance = new Lenis({
      smoothWheel: true,
      /** Càng nhỏ càng “trôi” lâu sau khi ngừng lăn (mặc định Lenis ~0.075–0.1) */
      lerp: 0.065,
      wheelMultiplier: 0.95,
      touchMultiplier: 1,
      autoRaf: true,
      stopInertiaOnNavigate: true,
    });
    setLenis(instance);
    return () => {
      instance.destroy();
      setLenis(null);
    };
  }, []);

  useEffect(() => {
    if (!lenis) return;

    const locationKey = location.key;

    /** Splash / load overlay trên Home: không scroll tới hash trước — giữ top; sau khi splash xong mới tới section */
    if (location.pathname === "/" && homeHashScrollBlocked) {
      lenis.scrollTo(0, { immediate: true, force: true });
      lenis.stop();
      return () => {
        scrollPositions.set(locationKey, window.scrollY);
        lenis.start();
      };
    }

    lenis.start();

    const { hash } = location;
    if (!hash) {
      const restoredY = navigationType === "POP" ? scrollPositions.get(locationKey) : undefined;
      lenis.scrollTo(restoredY ?? 0, { immediate: true });
      return () => {
        scrollPositions.set(locationKey, window.scrollY);
      };
    }
    const id = hash.replace("#", "");
    requestAnimationFrame(() => {
      const el = document.getElementById(id);
      if (el) lenis.scrollTo(el, { offset: -NAVBAR_OFFSET });
    });
    return () => {
      scrollPositions.set(locationKey, window.scrollY);
    };
  }, [lenis, location.key, location.pathname, location.hash, homeHashScrollBlocked, navigationType]);

  return <LenisContext.Provider value={lenis}>{children}</LenisContext.Provider>;
}
